//#region Imports
import React, { useState, useEffect, Fragment, useRef } from "react";
import sharedDatas from "../../businessLogic/sharedDatas";
import gainsightService from "../../businessLogic/gainsightService";
import { useNavigate } from "react-router-dom";

//oneFront SDK
import {
  useSnackbar,
  useQuery,
  useMutation,
  useTranslation,
} from "@1f/react-sdk";

import VaporPage from "@vapor/react-custom/VaporPage";
import VaporTag from "@vapor/react-custom/VaporTag";

//Vapor Components
import Stack from "@vapor/react-material/Stack";
import Button from "@vapor/react-material/Button";
import TextField from "@vapor/react-material/TextField";
import Autocomplete from "@vapor/react-material/Autocomplete";
import Alert from "@vapor/react-material/Alert";
import AccordionSummary from "@vapor/react-material/AccordionSummary";
import AccordionDetails from "@vapor/react-material/AccordionDetails";
import Grid from "@vapor/react-material/Grid";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import ExtendedAccordion from "@vapor/react-extended/ExtendedAccordion";

//Icons
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

//Custom components
import { Loading } from "../../components/Loading";
import { FascicoloSearchResultsGrid } from "./components/FascicoloSearchResultsGrid";

//Constants, Api and Data Models
import {
  bsaFascicolo,
  bsaAnagrafica,  
} from "../../businessLogic/constants/securityAreas";

import {
  pathFascicoloHome,
  pathFascicoloAdd
} from "../../businessLogic/constants/path";

import { setLocalStorageSelectedFascicolo } from "../../businessLogic/helpers/storageHelpers";
import {
  TIPO_DOCUMENTO_CARTA_IDENTITA_KEY,
  TIPO_DOCUMENTO_VISURA_CAMERALE_KEY,
} from "../../businessLogic/constants/baseTableKeys";
import { PAESE_IDENT_ITALIA } from "../../businessLogic/constants/baseTableKeys";
import {
  WS_CLASSIFIER_COMPANY,
  WS_CLASSIFIER_PERSON,
  WS_CLASSIFIER_SELF_EMPLOYED_WORKER,
  WS_OFFICE_TYPE_MAIN,
  WS_OFFICE_TYPE_SECONDARY,
  WS_VERIFIED_WITH_OTHER,
  GetPepRuoloFromWsPoliticalOffice,
  GetTipoDocumentoKeyFromWsVerifiedWith,
} from "../../businessLogic/constants/workspaceEnums";
import { removeBlanks, normalizeLabel } from "../../businessLogic/helpers/stringHelpers";
import {
  getDataFromFiscalCode,
  isValidCodiceFiscale,
  isValidItalianVAT,
} from "../../businessLogic/helpers/anagraficaHelpers";
import {
  fascicoloModel,
  anagraficaModel,
  baseTipoAnagraficaModel,
} from "../../businessLogic/models";

import {
  query_anagrafiche_byCf,
  query_anagrafiche_byCf_PG,
} from "../../businessLogic/query/anagraficaQueries";

import {
  query_base_tipo_anagrafica,
  query_base_tipo_documento,
  query_base_genere,
  query_base_provincia,
  query_base_comune_forCerved,
  query_base_stdcodes_byCodISTAT,
  query_base_comune_byCodBelfiore,
  query_base_paese_byCodAt
} from "../../businessLogic/query/baseTablesQueries";

import {WorkspaceApi} from "../../businessLogic/api/workspaceApi";
import { CervedApi } from "../../businessLogic/api/cervedApi";
import {AddressApi} from "../../businessLogic/api/addressApi";
import BaseTableApi from "../../businessLogic/api/baseTableApi";
import {
  mutation_add_audit
} from "../../businessLogic/mutations/auditMutations";
import {
  mutation_add_allegato
} from "../../businessLogic/mutations/allegatiMutations";

import {createAnagraficaModel,createFascicoloModel} from "../../businessLogic/helpers/modelHelpers";
import { AnagraficaApi, useAnagraficaSearchQuery } from "../../businessLogic/api/anagraficaApi";
//Other components
import { v4 as uuidv4 } from "uuid";
import { DateHelpers } from "../../businessLogic/helpers/dateHelpers";
import UserApi from "../../businessLogic/api/userApi";
import { LogApi } from "../../businessLogic/api/logApi";
import { FascicoloApi } from "../../businessLogic/api/fascicoloApi";
import { AnagraficaFascicoloApi } from "../../businessLogic/api/anagraficaFascicoloApi";
import { FascicoloHelpers } from "../../businessLogic/helpers/fascicoloHelpers";
import { AdeApi } from "../../businessLogic/api/AdeApi";
import {
  findClientFascicolo,
  getExistingRowFascicolo,
  excludeExistingSearchRows,
  matchesSearchSubjectType,
} from "../../businessLogic/helpers/fascicoloSearchHelpers";
//#endregion

export const FascicoloNew = ({ basePath }) => {
  const tenantId = sharedDatas.getPropertyByName("tenantId");
  const aziendaId = sharedDatas.getPropertyByName("aziendaId");
  const loggedUserId = sharedDatas.getPropertyByName("userId");
  const environment = sharedDatas.getPropertyByName("environment");
  const workspaceApi = WorkspaceApi();
  const cervedApi = CervedApi();
  const addressApi = AddressApi();
  const anagraficaApi = AnagraficaApi();
  const dateHelpers = DateHelpers();
  const fascicoloApi = FascicoloApi();
  const fascicoloHelpers = FascicoloHelpers();
  const anagFascicoloApi = AnagraficaFascicoloApi();
  const adeApi = AdeApi()
  const userApi = UserApi();
  const baseTableApi = BaseTableApi();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const logApi = LogApi();
  const companyRef = useRef(null);
  const comuniRequestRef = useRef(0);

  //#region Query
  const apiBaseProvincia = useQuery(
    query_base_provincia,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBaseComune = useQuery(
    query_base_comune_forCerved,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBaseStdcodesByCodISTAT = useQuery(
    query_base_stdcodes_byCodISTAT,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiAnagByCf = useAnagraficaSearchQuery(query_anagrafiche_byCf);
  const apiAnagByCfPG = useAnagraficaSearchQuery(query_anagrafiche_byCf_PG);
  const apiBaseTipoAnagrafica = useQuery(
    query_base_tipo_anagrafica,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBaseTipoDocumento = useQuery(
    query_base_tipo_documento,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBaseGenere = useQuery(
    query_base_genere,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBasePaeseByCodAt = useQuery(
    query_base_paese_byCodAt,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBaseComuneByCodBelfiore = useQuery(
    query_base_comune_byCodBelfiore,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  //#endregion

  //#region Mutation
  const apiAddAudit = useMutation(mutation_add_audit, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiAddAllegato = useMutation(mutation_add_allegato, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  // const apiUploadAllegatoToStorage = useMutation(
  //   mutation_upload_allegato_toStorage,
  //   { graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  // );
  //#endregion

  //#region UseStates
  const [backDropOpen, setBackDropOpen] = useState(true); //Loader utilizzato durante il caricamento
  const [isDataLoaded, setIsDataLoaded] = useState(false); //Indica lo stato di caricamento del result Api
  const [, setBaseTipoAnagList] = useState([]);
  const [baseTipoDocList, setBaseTipoDocList] = useState([]);
  const [professionistiAziendaList, setProfessionistiAziendaList] = useState([]);
  const [currentAnagrafica, setCurrentAnagrafica] = useState(
    new anagraficaModel(
      0,
      "",
      "",
      "",
      "",
      new Date(),
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      false,
      false,
      new Date(),
      1,
      new Date(),
      0,
      false,
      89,
      null,
      null,
      null,
      null,
      null,
      false,
      null,
      "",
      loggedUserId,
      "",
      new Date(),
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      1,
      1,
      0,
      "",
      "",
      "",
      "",
      false,
      "",
      "IT",
      "",
      null,
      "",
      1,
      0,
      "",
      "",
      new Date(),
      null,
      "",
      "",
      loggedUserId,
      new Date(),
      0,
      []   
    )
  ); //Contiene l'anagrafica corrente
  const [anagResultList, setAnagResultList] = useState([]);
  const [anagWSResultList, setAnagWSResultList] = useState([]);
  // Lista unica normalizzata (PARS-1402): righe con campo `source` (db|wks|cerved),
  // consumata dalla griglia risultati (Step 5). Convive con gli state accordion
  // finché lo Step 6 non li sostituisce.
  const [unifiedResults, setUnifiedResults] = useState([]);
  // Cerved viene interrogato solo su richiesta ("Estendi a Ex Cerved") o in automatico
  // quando Archivio + Workspace non restituiscono risultati.
  const [cervedSearched, setCervedSearched] = useState(false);
  const [searchProvincia, setSearchProvincia] = useState(null);
  const [searchComune, setSearchComune] = useState(null);
  const [baseProvinciaList, setBaseProvinciaList] = useState([]);
  const [baseComuneList, setBaseComuneList] = useState([]);
  const [comuniLoading, setComuniLoading] = useState(false);
  const [importResultList, setImportResultList] = useState([]);
  const [anagResultFascicolo, setAnagResultFascicolo] = useState(
    new fascicoloModel(
      0,
      tenantId,
      aziendaId,
      "",
      null,
      false,
      null,
      null,
      [],
      "",
      "",
      loggedUserId
    )
  );
  const [showNoAnagFound, setShowNoAnagFound] = useState(false);
  const [anagFound, setAnagFound] = useState(false);
  const [anagWSFound, setAnagWSFound] = useState(false);
  const [fascicoloFound, setFascicoloFound] = useState(false);
  const [isIdentifiedByEnableProf, setIsIdentifiedByEnableProf] = useState(true);
  const [userIdent, setUserIdent] = useState("")
  const [isPG, setIsPG] = useState(true);
  const [selectedValue, setSelectedValue] = useState("PG");
  const [disableSearch,setDisableSearch] = useState(false)
  // Esito validazione ADE (solo PROD): mostrato come chip non bloccante accanto ai campi
  const [adeCfStatus, setAdeCfStatus] = useState(null);
  const [adePIvaStatus, setAdePIvaStatus] = useState(null);
  //#endregion  

  useEffect(() => {
    loadDatas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoaded]);

  async function loadDatas() {
    try {
      setBackDropOpen(true);
      if (!isDataLoaded) {
        /**************************************/
        //Carico le tabelle di base
        //base_tipo_documento serve a risolvere il tipo documento che arriva da Workspace sulla
        //riga corrispondente: la tabella è per-tenant, quindi non si possono usare gli Id.
        const [
          tbTipoAnagResponse,
          tbTipoDocResponse,
          professionistiAzienda,
          provinciaResponse,
        ] = await Promise.all([
          apiBaseTipoAnagrafica.fetch({ tenantId: tenantId }),
          apiBaseTipoDocumento.fetch({ tenantId: tenantId }),
          userApi.GetUsersByCompanyId(),
          apiBaseProvincia.fetch({}),
        ]);

        const arrTipoAnagItems = [];
        tbTipoAnagResponse.data.data.base_tipo_anagrafica.map((item) =>
          arrTipoAnagItems.push(
            new baseTipoAnagraficaModel(
              item.Id,
              item.Key,
              item.Value,
              item.Order,
              item.IsSystem
            )
          )
        );
        setBaseTipoAnagList(arrTipoAnagItems);
        setBaseTipoDocList(baseTableApi.GetBaseTipoDocumento(tbTipoDocResponse));
        setProfessionistiAziendaList(professionistiAzienda);
        setBaseProvinciaList(baseTableApi.GetBaseProvincia(provinciaResponse));
      }
    } catch (e) {
      logApi.AddLog(e.message,e.stack,"loadDatas", "FascicoloNew")
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
      setIsDataLoaded(true);
    }
  }

  //Home Button
  const handleGoHome = () => {
    navigate(pathFascicoloHome);
  };

  //#region Steps
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed] = React.useState({});
  const steps = ["Ricerca", "Risultato"];
  const completedSteps = () => {
    return Object.keys(completed).length;
  };
  const totalSteps = () => {
    return steps.length;
  };
  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };
  const isLastStep = () => {
    return activeStep === steps.length - 1;
  };
  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  //#endregion

  //#region Form
  const handleChange = (event, value) => {
    const id = event.target.id !== undefined ? event.target.id : event.target.name;
    
    let cf = currentAnagrafica.cf;
    let pIva = currentAnagrafica.pIva;
    let nome = currentAnagrafica.nome;
    let cognome = currentAnagrafica.cognome;
    let denominazione = currentAnagrafica.denominazione;

    if (id === "checkPg") {
      setSelectedValue("PG")
      currentAnagrafica.isPg = true;
      setIsPG(true);
    } else if (id === "checkPf"){
      setSelectedValue("PF")
      currentAnagrafica.isPg = false;
      setIsPG(false);
    } else if( id === "checkDi") {
      setSelectedValue("DI")
      currentAnagrafica.isPg = true
      setIsPG(true)
    } else if (id === "txtSearchCf") {
        cf = selectedValue === "PG" ? event.target.value.replace(/[^\d]/g, "") : event.target.value;
        setAdeCfStatus(null);
    } else if (id === "txtSearchPIva") {
      pIva = event.target.value;
      setAdePIvaStatus(null);
    } else if (id === "txtSearchNome") {
      nome = event.target.value;
    } else if (id === "txtSearchCognome") {
      cognome = event.target.value;
    } else if (id === "txtSearchDenominazione") {
      denominazione = event.target.value;
    } else if (id === "txtCf") {
      cf = event.target.value;
      setAdeCfStatus(null);
    } else if (id === "txtPIva") {
      pIva = event.target.value;
      setAdePIvaStatus(null);
    } else if (id === "txtNome") {
      nome = event.target.value;
    } else if (id === "txtCognome") {
      cognome = event.target.value;
    } else if (id === "txtDenominazione") {
      denominazione = event.target.value;
    }

    setCurrentAnagrafica((prevState) => ({
      ...prevState,
      isPg: isPG,
      cf: cf,
      pIva: pIva,
      nome: nome,
      cognome: cognome,
      denominazione: denominazione,
    }));
  };
  // Segmented control tipo soggetto (PG / DI / PF) in sostituzione delle 3 radio
  const resetSearchGeography = () => {
    comuniRequestRef.current += 1;
    setSearchProvincia(null);
    setSearchComune(null);
    setBaseComuneList([]);
    setComuniLoading(false);
  };
  const handleSearchProvinciaChange = async (event, provincia) => {
    resetSearchGeography();
    const requestId = comuniRequestRef.current;
    setSearchProvincia(provincia);
    if (!provincia) return;
    setComuniLoading(true);
    try {
      const response = await apiBaseComune.fetch({ provincia: provincia.sigla });
      if (requestId !== comuniRequestRef.current) return;
      const comuneRows = response.data.data.base_comune ?? [];
      const codici = [...new Set(comuneRows.map((comune) => comune.Codice).filter(Boolean))];
      const standardResponse = codici.length > 0
        ? await apiBaseStdcodesByCodISTAT.fetch({ codici })
        : null;
      if (requestId !== comuniRequestRef.current) return;
      const baseComuneResponse = {
        data: {
          data: {
            base_comune: comuneRows,
            base_stdcodes_comune: standardResponse?.data?.data?.base_stdcodes_comune ?? [],
          },
        },
      };
      setBaseComuneList(baseTableApi.GetBaseComune(baseComuneResponse));
    } catch (error) {
      if (requestId !== comuniRequestRef.current) return;
      logApi.AddLog(error.message, error.stack, "handleSearchProvinciaChange", "FascicoloNew");
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      if (requestId === comuniRequestRef.current) setComuniLoading(false);
    }
  };
  const filterGeographyOptions = (options, state) =>
    options.filter((option) =>
      option.nome.toLowerCase().startsWith(state.inputValue.toLowerCase())
    );
  const resetSearchResults = () => {
    setUnifiedResults([]);
    setAnagResultList([]);
    setAnagWSResultList([]);
    setImportResultList([]);
    setShowNoAnagFound(false);
    setAnagFound(false);
    setAnagWSFound(false);
    setFascicoloFound(false);
    setCervedSearched(false);
    setIsIdentifiedByEnableProf(true);
    setUserIdent("");
  };
  const handleTipoSoggettoChange = (event, value) => {
    if (value === null || value === selectedValue) return;
    resetSearchResults();
    const nextIsPG = value === "PG" || value === "DI";
    setSelectedValue(value);
    setIsPG(nextIsPG);
    if (!nextIsPG) resetSearchGeography();
    setCurrentAnagrafica((prevState) => ({ ...prevState, isPg: nextIsPG }));
  };
  const handleBlur = async (event, value) => {
    try {
      const { id } = event.target;
      const txtValue = event.target.value;
      if(!txtValue||!id) return    
      
      if(environment === "PROD"){
        await checkFormForPROD(id,txtValue)
      } else {
        await checkFormForDEVTEST(id,txtValue)
      }      
    } catch (e) {
      logApi.AddLog(e.message,e.stack,"handleBlur", "FascicoloNew")      
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    }
  };
  const checkFormForDEVTEST = async (id,txtValue) => {
    if (id === "txtCf" || id ==="txtSearchCf") {
      //Per le PG il campo CF contiene la P.IVA (VAT numerico), quindi si valida come P.IVA
      const cfValid = isPG
        ? selectedValue === "PG"
          ? await anagraficaApi.VerifyPIva(txtValue)
          : await anagraficaApi.VerifyCf(txtValue)
        : await anagraficaApi.VerifyCf(txtValue);
      setAdeCfStatus({ valid: cfValid });
      if (!cfValid) {
        enqueueSnackbar("Il Codice Fiscale non sembra essere corretto", { variant: "warning" });
      }
    } else if (id === "txtPIva" || id === "txtSearchPIva") {
      const pIvaValid = await anagraficaApi.VerifyPIva(txtValue);
      setAdePIvaStatus({ valid: pIvaValid });
      if (!pIvaValid) {
        enqueueSnackbar("La Partita IVA non sembra essere corretta", { variant: "warning" });
      }
    }
  }
  const checkFormForPROD = async (id,txtValue) => {    
    let adeValidationPIvaResponse = null 
    let adeValidationCfResponse = null
    if (id === "txtCf" || id ==="txtSearchCf") {
      adeValidationCfResponse = await adeApi.validateCFFromADE(txtValue)
      const cfValid = !!adeValidationCfResponse?.valido
      setAdeCfStatus({ valid: cfValid })
      if(adeValidationCfResponse && !adeValidationCfResponse.valido){          
        enqueueSnackbar("Il codice fiscale inserito potrebbe essere formalmente errato o inesistente nelle banche dati ADE", { variant: "warning" });          
      }
    } else if (id === "txtPIva" || id === "txtSearchPIva") {
      adeValidationPIvaResponse = await adeApi.validatePIVAFromADE(txtValue)
      const pIvaValid = !!adeValidationPIvaResponse?.valida && adeValidationPIvaResponse?.status !== 400
      setAdePIvaStatus({ valid: pIvaValid })
      if(adeValidationPIvaResponse && (!adeValidationPIvaResponse.valida || adeValidationPIvaResponse.status === 400)){        
        enqueueSnackbar("La Partita IVA non è valida", { variant: "warning" });              
      }
    }

    //ADE non è più bloccante: l'esito è comunicato dal chip accanto al campo,
    //"Cerca" resta abilitato anche se CF/P.IVA non risultano validi in ADE.
    setCurrentAnagrafica({ ...currentAnagrafica, denominazione: adeValidationPIvaResponse?.denominazione ?? "" });
  }
  //#region Mappatura dati da Workspace
  /**
   * Risolve un indirizzo Workspace nelle chiavi delle tabelle base.
   *
   * Lo schema è sempre lo stesso, ma i valori arrivano in due codifiche diverse a
   * seconda del canale che ha scritto il record su Workspace (UI/sync applicativa
   * oppure backoffice Workspace):
   *  - per nome:    country "Italia", province "AOSTA",  city "CHALLAND SAINT ANSELME"
   *  - per codice:  country "",       province "SA",     city "A186" (cod. Belfiore)
   * Non dipende dal classifier: entrambe le forme possono arrivare sia su PERSON sia
   * su COMPANY, quindi la tolleranza va tenuta qui e non in un ramo per tipologia.
   * I resolver di addressApi riconoscono la forma e dispacciano di conseguenza.
   *
   * La provincia si ricava preferibilmente dalla sigla del comune, perché quando
   * Workspace la manda per nome usa il capoluogo ("AOSTA") e non il nome presente in
   * base_provincia ("Valle d'Aosta").
   *
   * Provincia e comune vengono valorizzati anche come testo, così sono leggibili
   * nell'elenco dei risultati anche per gli indirizzi italiani; i valori codificati
   * non vengono però mai riversati nei campi testuali, per non mostrare "A186 (SA)".
   */
  const resolveWsOfficeAddress = async (office) => {
    const emptyAddress = {
      paeseId: null,
      provinciaId: null,
      comuneId: null,
      provincia: "",
      comune: "",
    };
    if (!office) return emptyAddress;
    //Quando l'indirizzo è codificato Workspace omette il paese: si assume l'Italia,
    //perché gli indirizzi esteri arrivano sempre con l'esonimo valorizzato.
    const paese = office.country
      ? await addressApi.TryGetPaeseByNome(office.country)
      : await addressApi.TryGetPaeseByIdentificativo(PAESE_IDENT_ITALIA);
    //Per gli indirizzi esteri le tabelle base non hanno corrispondenze di
    //provincia/comune: restano le sole descrizioni testuali.
    if (paese && paese.Identificativo !== PAESE_IDENT_ITALIA) {
      return {
        ...emptyAddress,
        paeseId: paese.Id,
        provincia: office.province ?? "",
        comune: office.city ?? "",
      };
    }
    const comune = await addressApi.TryGetComuneByNomeOrCodBelfiore(office.city);
    const provincia =
      (comune ? await addressApi.TryGetProvinciaBySigla(comune.Provincia) : null) ??
      (await addressApi.TryGetProvinciaBySiglaOrNome(office.province));
    return {
      paeseId: paese?.Id ?? null,
      provinciaId: provincia?.Id ?? null,
      comuneId: comune?.Id ?? null,
      provincia: provincia?.Nome ?? (addressApi.isSiglaProvincia(office.province) ? "" : office.province ?? ""),
      comune: comune?.Nome ?? (addressApi.isCodBelfiore(office.city) ? "" : office.city ?? ""),
    };
  };
  /**
   * Workspace restituisce il luogo di nascita come codice Belfiore (es. "L328"),
   * che va risolto sul comune italiano o, in mancanza, sullo stato estero.
   * Ritornando "" si lascia intervenire il fallback che ricava il luogo dal codice
   * fiscale in handleCreateFascicolo.
   */
  const resolveWsBirthplace = async (birthplace) => {
    if (!birthplace) return "";
    try {
      const comune = await addressApi.GetMunicipalityByCodeBelfiore(birthplace);
      if (comune?.Nome) return comune.Nome;
    } catch (e) {
      //Non è un comune italiano: provo con gli stati esteri
    }
    try {
      const paese = await addressApi.GetPaeseByCodiceBelfiore(birthplace);
      if (paese?.Nome) return paese.Nome;
    } catch (e) {
      logApi.AddLog(`Codice Belfiore non risolvibile: ${birthplace}`, "", "resolveWsBirthplace", "FascicoloNew");
    }
    return "";
  };
  /**
   * Il tipo documento di default, usato quando Workspace non dà un valore utilizzabile:
   * "Visura camerale" per le persone giuridiche (comprese le ditte individuali),
   * "Carta d'identità" per le persone fisiche. Risolto per chiave, perché gli Id di
   * base_tipo_documento non sono garantiti fuori dal seed di sistema.
   */
  const getDefaultDocTipoId = (isPg) =>
    baseTipoDocList.find(
      (x) => x.key === (isPg ? TIPO_DOCUMENTO_VISURA_CAMERALE_KEY : TIPO_DOCUMENTO_CARTA_IDENTITA_KEY)
    )?.id ?? null;
  /**
   * La tendina "Verificato tramite" filtra le voci per personType, quindi una riga va
   * considerata solo se compatibile con la tipologia del soggetto: altrimenti si
   * persisterebbe un tipo documento che l'utente non potrebbe riselezionare a mano.
   */
  const isDocTipoCompatibile = (baseTipoDoc, isPg) =>
    !baseTipoDoc?.personType || baseTipoDoc.personType === (isPg ? "PG" : "PF");
  /**
   * Risolve il tipo documento a partire dai dati AML di Workspace.
   *
   * "antiMoneyLaundering.verifiedWith" è il documento con cui il soggetto è stato
   * verificato (è il corrispondente di docTipoId, non di modalitaIdentId), ma il suo
   * enum copre solo i documenti da persona fisica. Quando vale "OTHER" il documento
   * effettivo è descritto nel testo libero "other", che può corrispondere sia a una
   * voce di sistema sia a una voce aggiunta dal tenant: si cerca quindi per
   * denominazione. Se nulla aggancia si ricade sul default per tipologia di soggetto.
   */
  const resolveWsDocTipoId = (anagFromWs, isPgFromWs) => {
    const verifiedWith = anagFromWs.antiMoneyLaundering?.verifiedWith;
    const tipoDocKey = GetTipoDocumentoKeyFromWsVerifiedWith(verifiedWith);
    if (tipoDocKey) {
      const tipoDocByKey = baseTipoDocList.find((x) => x.key === tipoDocKey);
      if (tipoDocByKey && isDocTipoCompatibile(tipoDocByKey, isPgFromWs)) {
        return tipoDocByKey.id;
      }
    } else if (verifiedWith === WS_VERIFIED_WITH_OTHER) {
      const descrizioneAltro = normalizeLabel(anagFromWs.antiMoneyLaundering?.other);
      if (descrizioneAltro) {
        const tipoDocByValue = baseTipoDocList.find(
          (x) => normalizeLabel(x.value) === descrizioneAltro
        );
        if (tipoDocByValue && isDocTipoCompatibile(tipoDocByValue, isPgFromWs)) {
          return tipoDocByValue.id;
        }
      }
    }
    return getDefaultDocTipoId(isPgFromWs);
  };
  /**
   * "verifiedBy" arriva da Workspace come "Nome Cognome" (lo compone la sync in uscita
   * partendo da IdentifiedByUser), mentre in anagrafica serve lo userId. Risalgo quindi
   * al professionista dell'azienda corrente, con fallback sull'utente collegato.
   */
  const resolveWsIdentifiedByUser = (verifiedBy) => {
    if (!verifiedBy) return loggedUserId;
    const professionista = professionistiAziendaList.find(
      (x) => x.completeName?.trim().toLowerCase() === verifiedBy.trim().toLowerCase()
    );
    return professionista?.userId ?? loggedUserId;
  };
  /**
   * Costruisce il modello anagrafica a partire da un item Workspace (risposta di
   * getItem). Usata sia per i risultati della ricerca sia per creare la persona fisica
   * collegata a una ditta individuale, che su Workspace è un item separato.
   */
  const buildAnagraficaFromWsItem = async (anagFromWs) => {
    //Gli indirizzi si scelgono per officeType, non per posizione nell'array
    const wsOffices = anagFromWs.offices ?? [];
    const wsOfficeMain = wsOffices.find((x) => x.officeType === WS_OFFICE_TYPE_MAIN) ?? wsOffices[0] ?? null;
    const wsOfficeSecondary = wsOffices.find((x) => x.officeType === WS_OFFICE_TYPE_SECONDARY && x !== wsOfficeMain) ?? null;
    const wsAddress1 = await resolveWsOfficeAddress(wsOfficeMain);
    const wsAddress2 = await resolveWsOfficeAddress(wsOfficeSecondary);
    //Workspace manda il luogo di nascita come codice Belfiore, non come nome
    const wsNascitaLuogo = await resolveWsBirthplace(anagFromWs.personalData.birthplace);
    //Una ditta individuale è a tutti gli effetti un cliente persona giuridica: porta
    //denominazione e partita iva, e da lì discendono tipo anagrafica e tipo documento.
    const isPgFromWs =
      anagFromWs.classifier === WS_CLASSIFIER_COMPANY ||
      anagFromWs.classifier === WS_CLASSIFIER_SELF_EMPLOYED_WORKER;
    return new anagraficaModel(
      0,
      anagFromWs.personalData.taxIdentifier ?? "",
      anagFromWs.personalData.name ?? "",
      anagFromWs.personalData.surname ?? "",
      wsNascitaLuogo,
      anagFromWs.personalData.birthDate,
      anagFromWs.antiMoneyLaundering?.occupationalActivity ?? "",
      anagFromWs.personalData.businessName ?? "",
      anagFromWs.personalData.vatIdentifier ?? "",
      "",
      "",
      wsAddress1.provincia,
      wsAddress1.comune,
      wsOfficeMain?.streetName ?? "",
      wsOfficeMain?.streetNumber ?? "",
      wsOfficeMain?.zipCode ?? "",
      wsAddress2.provincia,
      wsAddress2.comune,
      wsOfficeSecondary?.streetName ?? "",
      wsOfficeSecondary?.streetNumber ?? "",
      wsOfficeSecondary?.zipCode ?? "",
      anagFromWs.antiMoneyLaundering?.politicallyExposedPerson ?? false,
      false,
      new Date(),
      1,
      new Date(),
      0,
      isPgFromWs,
      wsAddress1.paeseId,
      wsAddress1.provinciaId,
      wsAddress1.comuneId,
      wsAddress2.paeseId,
      wsAddress2.provinciaId,
      wsAddress2.comuneId,
      anagFromWs.deleted ?? false,
      //Il genere arriva solo per le persone fisiche e per le ditte individuali; per le
      //società Workspace lo manda nullo, quindi non serve un ramo sul classifier.
      anagFromWs.personalData.gender === "M" ? 1 : anagFromWs.personalData.gender === "F" ? 2 : null,
      "",
      loggedUserId,
      null,
      null,
      tenantId,
      "",
      anagFromWs.id,
      anagFromWs.antiMoneyLaundering?.id ?? null,
      wsOfficeMain?.id ?? null,
      wsOfficeSecondary?.id ?? null,
      //Workspace manda politicalOfficeHeld anche per i soggetti non PEP:
      //va ignorato, altrimenti si persiste un ruolo politico inesistente.
      anagFromWs.antiMoneyLaundering?.politicallyExposedPerson
        ? GetPepRuoloFromWsPoliticalOffice(anagFromWs.antiMoneyLaundering?.politicalOfficeHeld)
        : "",
      null,
      null,
      0,
      "",
      "",
      "",
      "",
      false,
      "",
      anagFromWs.personalData.countryCode,
      anagFromWs.status,
      null,
      anagFromWs.revision,
      //modalitaIdentId: "Diretta". Workspace non ha un corrispondente della modalità
      //di identificazione: "antiMoneyLaundering.verifiedWith", che sembrerebbe il
      //candidato, è in realtà il tipo documento (vedi resolveWsDocTipoId).
      1,
      //docTipoId, ricavato da verifiedWith / other
      resolveWsDocTipoId(anagFromWs, isPgFromWs),
      "",
      "",
      new Date(),
      null,
      "",
      "",
      resolveWsIdentifiedByUser(anagFromWs.antiMoneyLaundering?.verifiedBy),
      new Date(),
      0,
      [],
      null,
      null
    );
  };
  //#endregion
  // Normalizza un anagraficaModel (Archivio o Workspace) in una riga di griglia con
  // provenienza. `meta` porta i dati usati dai controlli alla selezione (Step 6):
  // già cliente stessa azienda, fascicolo collegato, professionista identificatore.
  const buildUnifiedRowFromAnag = (anag, source, meta = {}) => ({
    rowId: `${source}-${anag.workspaceId || anag.cf || anag.pIva || uuidv4()}`,
    source, // "db" | "wks"
    isPg: anag.isPg,
    denominazione: anag.isPg
      ? anag.denominazione || ""
      : `${anag.nome || ""} ${anag.cognome || ""}`.trim(),
    codiceFiscale: anag.cf || "",
    partitaIva: anag.pIva || "",
    sede: anag.addrComune
      ? `${anag.addrComune}${anag.addrProvincia ? ` (${anag.addrProvincia})` : ""}`
      : anag.addrProvincia || "",
    stato: anag.status || "",
    anagrafica: anag,
    cervedIdSoggetto: null,
    meta,
  });
  // Normalizza un CervedEntitySearchItem in una riga di griglia. L'anagrafica vera
  // viene composta solo alla selezione, via arricchimento visura (Step 8/9).
  const buildUnifiedRowFromCerved = (item) => {
    const indirizzoSede = [item.indirizzo, item.comune].filter(Boolean).join(", ");
    return {
      rowId: `cerved-${item.idSoggetto}`,
      source: "ModeFinance",
      isPg: !item.isPerson,
      denominazione: item.isPerson
        ? `${item.nome || ""} ${item.cognome || ""}`.trim() || item.denominazione || ""
        : item.denominazione || "",
      codiceFiscale: item.codiceFiscale || "",
      isDI : !item.isPerson && isValidCodiceFiscale(item.codiceFiscale),
      partitaIva: item.partitaIva || "",
      sede: [indirizzoSede, item.provincia ? `(${item.provincia})` : ""]
        .filter(Boolean)
        .join(" "),
      stato: item.statoAttivita || "",
      anagrafica: null,
      cervedIdSoggetto: item.idSoggetto,
      cervedItem: item,
      meta: {},
    };
  };
  // Interroga Ex Cerved (ricerca avanzata PARS-1402) mappando CF/P.IVA valorizzati in
  // AND su CervedAdvancedSearchCriteria e accoda le righe `cerved` alla lista unica.
  // Soft-fail: se la feature è disabilitata (503) l'API ritorna null e non aggiunge nulla.
  const runCervedSearch = async ({ cf, pIva, denominazione, unified = [] } = {}) => {
    if (!isPG) return [];
    try {
      const criteria = {
        codiceFiscale: cf || null,
        partitaIva: pIva || null,
        denominazione: denominazione || null,
        codiceProvincia: isPG ? searchProvincia?.sigla || null : null,
        codiceIstatComune: isPG ? searchComune?.codiceIstatStorico || null : null,
      };
      const cervedResponse = await cervedApi.SearchEntitiesAdvanced(criteria);
      setCervedSearched(true);
      const items = cervedResponse?.items ?? [];
      if (items.length <= 0) return [];
      let cervedRows = items.map(buildUnifiedRowFromCerved);
      // Filtra per la tipologia selezionata in alto (PG/DI); le persone fisiche non sono interessate.
      if (selectedValue === "PG") {
        cervedRows = cervedRows.filter((row) => !row.isDI);
      } else if (selectedValue === "DI") {
        cervedRows = cervedRows.filter((row) => row.isDI);
      }
      cervedRows = excludeExistingSearchRows(cervedRows, unified);
      setUnifiedResults((prev) => [...prev, ...cervedRows]);
      return cervedRows;
    } catch (e) {
      logApi.AddLog(e.message, e.stack, "runCervedSearch", "FascicoloNew");
      enqueueSnackbar(e.message || t("error.generale"), { variant: "error" });
      return [];
    }
  };
  const handleSearchClick = async () => {
    try {            
      setBackDropOpen(true);      
      setAnagResultList([]);
      setAnagWSResultList([]);
      setAnagResultFascicolo(
        new fascicoloModel(
          0,
          tenantId,
          aziendaId,
          "",
          null,
          false,
          null,
          null,
          [],
          "",
          "",
          loggedUserId
        )
      );
      setShowNoAnagFound(false);
      setAnagFound(false);
      setAnagWSFound(false);
      setFascicoloFound(false);
      setUnifiedResults([]);
      setCervedSearched(false);

      let cf = currentAnagrafica.cf ? removeBlanks(currentAnagrafica.cf) : "";
      let pIva = currentAnagrafica.pIva ? removeBlanks(currentAnagrafica.pIva) : "";      
      const nome = (currentAnagrafica.nome || "").trim();
      const cognome = (currentAnagrafica.cognome || "").trim();
      // Denominazione (solo PG/DI): consente la ricerca anche senza CF/P.IVA.
      let denominazione = currentAnagrafica.denominazione
        ? currentAnagrafica.denominazione.trim()
        : "";

      let canIProceed = true;
      if (isPG) {
        // Nessun vincolo di formato/lunghezza su CF/P.IVA: basta un criterio valorizzato.
        if(cf === "" && pIva === "" && denominazione === ""){
          enqueueSnackbar("Inserisci almeno un criterio di ricerca: Codice Fiscale, Partita IVA o Denominazione", {
            variant: "warning",
          });
          canIProceed = false
        }
      } else {
        if (cf === "" && nome === "" && cognome === "") {
          enqueueSnackbar("Inserisci almeno un criterio di ricerca: Codice Fiscale, Nome o Cognome", {
            variant: "warning",
          });
          canIProceed = false
        }
      }

      if (canIProceed) {
        // Valori grezzi (senza wildcard) per i criteri di ricerca Cerved.
        const rawCf = cf;
        const rawPIva = pIva;
        const rawDenominazione = denominazione;
        cf = "%" + cf + "%";
        pIva = "%" + pIva + "%";        
        denominazione = "%" + denominazione + "%";

        /**************************************/
        //Cerco nelle anagrafiche
        const arrAnag = [];
        // Righe normalizzate Archivio con la relativa provenienza per la griglia unica.
        const arrUnifiedDb = [];
        let anagByCfResponse = null;              
        if (!isPG) {
          anagByCfResponse = await apiAnagByCf.fetch({
            tenantId: tenantId,
            cf: cf,
            nome: "%" + nome + "%",
            cognome: "%" + cognome + "%",
          });
        } else {
          anagByCfResponse = await apiAnagByCfPG.fetch({
            tenantId: tenantId,
            cf: cf,
            pIva: pIva,
            denominazione: denominazione,
            addrFilter: {
              ...(searchProvincia && { AddrProvinciaId: { _eq: searchProvincia.id } }),
              ...(searchComune && { AddrComuneId: { _eq: searchComune.id } }),
            },
          });
        }        

        if (
          anagByCfResponse !== null &&
          anagByCfResponse.data.data.anagrafica.length > 0
        ) {
          for (
            let i = 0;
            i < anagByCfResponse.data.data.anagrafica.length;
            i++
          ) {
            const item = anagByCfResponse.data.data.anagrafica[i];
            const isDI = item.IsPg && isValidCodiceFiscale(item.Cf);
            if (!matchesSearchSubjectType(item.IsPg, isDI, selectedValue)) continue;

            if (arrAnag.filter((x) => x.cf === item.Cf).length <= 0) {                            
              const anagAv = createAnagraficaModel(item)              
              arrAnag.push(anagAv);

              const isOfSameAzienda =
                item.anagrafica_fascicolos.filter(
                  (x) =>
                    String(x.fascicolo.AziendaId) === String(aziendaId)
                ).length > 0;

              const isProfessionistaOfSameAzienda = professionistiAziendaList.filter(x=> x.userId === item.IdentifiedByUser).length > 0;
              setIsIdentifiedByEnableProf(isProfessionistaOfSameAzienda);
              // Metadati di riga usati alla selezione (Step 6): permessi identificatore,
              // fascicolo già cliente della stessa azienda, "vai al fascicolo".
              let rowUserIdent = "";
              let rowFascicolo = null;
              if(!isProfessionistaOfSameAzienda && item.IdentifiedByUser){
                let { userResponse } = await userApi.GetUserById(item.IdentifiedByUser)
                rowUserIdent = userResponse?.Name + " " + userResponse?.Surname || "";
                setUserIdent(rowUserIdent);
              }
              setFascicoloFound(isOfSameAzienda);              
              if (isOfSameAzienda) {
                // Il fascicolo esistente va proposto in apertura solo se l'anagrafica
                // è già cliente (PF/PG) su un fascicolo DELL'AZIENDA CORRENTE; se è
                // cliente solo su un'altra azienda si deve proporre la creazione.
                const fascAsCliente = findClientFascicolo(item.anagrafica_fascicolos, aziendaId);
                if(fascAsCliente) {
                  rowFascicolo = new fascicoloModel(
                    fascAsCliente.Id,
                    tenantId,
                    aziendaId,
                    fascAsCliente.Name,
                    fascAsCliente.Date,
                    false,
                    null,
                    null,
                    [],
                    "",
                    "",
                    fascAsCliente.CreatedBy,
                    fascAsCliente.ModifiedBy
                  );
                  setAnagResultFascicolo(rowFascicolo);
                } else  {                                    
                  setFascicoloFound(false)                  
                }
              }
              arrUnifiedDb.push(
                buildUnifiedRowFromAnag(anagAv, "db", {
                  isOfSameAzienda,
                  fascicolo: rowFascicolo,
                  isIdentifiedByEnableProf: isProfessionistaOfSameAzienda,
                  userIdent: rowUserIdent,
                  identifiedByUser: item.IdentifiedByUser,
                })
              );
            }
          }
        }
        setAnagResultList(arrAnag);
        setAnagFound(arrAnag.length > 0);

        /**************************************/
        //Cerco nelle anagrafiche da Workspace
        let arrAnagWS = [];
        const arrUnifiedWs = [];
        try {
          //TODO : Ricerca per CF/PIVA
          //Controllare a monte prima della ricerca se abbiamio una PIVA o un CF di modo da fare la ricerca già
          //contestualizzata per "Company" (ditta individuale o persona giuridica) o "Person" (persona fisica)

          //Chiamata per la ricerca. Termine: CF se presente, altrimenti P.IVA o denominazione.
          const wsSearchTerm = isPG ? rawCf || rawPIva || rawDenominazione : rawCf;
          let anagWSResponse = arrAnag.length === 0 && wsSearchTerm
            ? await workspaceApi.SearchItem(wsSearchTerm)
            : null;

          //Filtro i risultati di workspace secondo la tipologia scelta. La ditta
          //individuale ha un classifier suo: cercando per codice fiscale Workspace
          //restituisce sia la ditta sia la persona fisica omonima, e qui va tenuta
          //solo la prima (la persona fisica viene poi collegata come titolare
          //effettivo e legale rappresentante in fase di creazione).
          const wsClassifierWanted =
            selectedValue === "DI"
              ? WS_CLASSIFIER_SELF_EMPLOYED_WORKER
              : isPG
              ? WS_CLASSIFIER_COMPANY
              : WS_CLASSIFIER_PERSON;
          if(anagWSResponse && anagWSResponse.data.data.searchItem.content.length > 0){
            // Dedup: escludo dai risultati Workspace le anagrafiche già presenti in
            // Archivio a parità di Wks ID (il DB conserva il workspaceId di origine).
            const dbWorkspaceIds = new Set(
              arrAnag.map((x) => x.workspaceId).filter(Boolean)
            );
            const wsItemsFound = anagWSResponse.data.data.searchItem.content.filter(
              (x) => x.classifier === wsClassifierWanted && !dbWorkspaceIds.has(x.id)
            );

            for (let i = 0; i < wsItemsFound.length; i++) {
              const anagraficaFromSearch = wsItemsFound[i];
              let completeAnagFromWs = await workspaceApi.GetItem(anagraficaFromSearch.id)
              let anagFromWs = completeAnagFromWs?.data.data.getItem ?? null
              if(!anagFromWs) continue
              const anagWs = await buildAnagraficaFromWsItem(anagFromWs);
              const wsRow = buildUnifiedRowFromAnag(anagWs, "wks");
              if (excludeExistingSearchRows([wsRow], [...arrUnifiedDb, ...arrUnifiedWs]).length === 0) continue;
              arrAnagWS.push(anagWs);
              arrUnifiedWs.push(wsRow);
            }

            setAnagWSResultList(arrAnagWS);
            setAnagWSFound(arrAnagWS.length > 0);
          }

        } catch (e) {
          logApi.AddLog(e.message,e.stack,"handleSearchClick", "FascicoloNew")
          setAnagWSResultList([]);
          setAnagWSFound(false);
        }

        /**************************************/
        // Compongo la lista unica normalizzata (Archivio + Workspace).
        const unified = [...arrUnifiedDb, ...arrUnifiedWs];
        setUnifiedResults(unified);

        // Ex Cerved: interrogato automaticamente solo se Archivio + Workspace non
        // hanno prodotto risultati. Altrimenti resta a disposizione dell'utente
        // tramite il pulsante "Estendi a Ex Cerved" (Step 5/6).
        let cervedRows = [];
        if (unified.length <= 0 && isPG) {
          cervedRows = await runCervedSearch({ cf: rawCf, pIva: rawPIva, denominazione: rawDenominazione, unified: unified });
        }
        setShowNoAnagFound(unified.length <= 0 && cervedRows.length <= 0);
        handleNext();
      }
    } catch (e) {
      logApi.AddLog(e.message,e.stack,"handleSearchClick", "FascicoloNew")      
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
    }
  };
  // Estensione manuale della ricerca a Ex Cerved (pulsante dedicato, Step 5/6):
  // accoda le righe `cerved` alla griglia già popolata da Archivio + Workspace.
  const handleExtendToCerved = async () => {
    try {
      setBackDropOpen(true);
      const cf = currentAnagrafica.cf ? removeBlanks(currentAnagrafica.cf) : "";
      const pIva = currentAnagrafica.pIva ? removeBlanks(currentAnagrafica.pIva) : "";
      const denominazione = currentAnagrafica.denominazione ? currentAnagrafica.denominazione.trim() : "";
      const cervedRows = await runCervedSearch({ cf, pIva, denominazione, unified: unifiedResults });
      if (cervedRows.length <= 0) {
        enqueueSnackbar(t("fascicolo.new.cerved.noResults", "Nessun risultato da Modefinance"), {
          variant: "info",
        });
      }
    } catch (e) {
      logApi.AddLog(e.message, e.stack, "handleExtendToCerved", "FascicoloNew");
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
    }
  };
  // Selezione riga della griglia risultati (Step 6): attiva gli stessi controlli
  // eseguiti oggi quando si rileva un dato già presente.
  const handleRowSelect = (row) => {
    if (!row) return;
    if (row.source === "ModeFinance") {
      handleSelectCervedRow(row);
      return;
    }
    // Righe Archivio/Workspace: porto i controlli di riga negli state condivisi usati
    // dai downstream (permessi identificatore, fascicolo già cliente stessa azienda).
    const meta = row.meta || {};
    setIsIdentifiedByEnableProf(meta.isIdentifiedByEnableProf !== false);
    setUserIdent(meta.userIdent || "");
    // Identificata da un professionista senza permessi sul soggetto obbligato corrente.
    if (meta.identifiedByUser && meta.isIdentifiedByEnableProf === false) {
      enqueueSnackbar(
        t(
          "fascicolo.new.search.identifiedByNoPerm",
          "Non è possibile creare il fascicolo: l'anagrafica è stata identificata da un professionista senza permessi sul soggetto obbligato corrente"
        ),
        { variant: "warning" }
      );
      return;
    }
    // Già cliente della stessa azienda su un fascicolo → vai al fascicolo.
    const existingFascicolo = getExistingRowFascicolo(row);
    if (existingFascicolo) {
      setAnagResultFascicolo(existingFascicolo);
      handleGoToFascicolo(existingFascicolo.id);
      return;
    }
    // Altrimenti prosegue con la creazione del fascicolo dall'anagrafica selezionata.
    handleCreateFascicolo(row.anagrafica, files.length > 1, "");
  };
  // Selezione di una riga Ex Cerved: l'arricchimento visura (profile/contacts/
  // connections/beneficialOwners) e il mapping sull'anagraficaModel sono implementati
  // negli Step 8/9.
  const handleSelectCervedRow = async (row) => {
    try{
      let comuneResponse = await addressApi.TryGetComuneByNomeOrCodBelfiore(row.cervedItem.comune);
      let provinciaResponse = await addressApi.TryGetProvinciaBySiglaOrNome(row.cervedItem.provincia);
      let comuneId = comuneResponse?.Id ?? null;
      let provinciaId = provinciaResponse?.Id ?? null;

      let profileResponse = await cervedApi.GetProfile(row.cervedItem.idSoggetto);
      let baseAteco = await baseTableApi.GetBaseAteco();
      let atecoFinded = baseAteco.find(a => a.atecoCode === profileResponse.codiceAteco) ?? baseAteco.find(a => a.atecoTitle === profileResponse.descrizioneAteco);

      let cervedAnag = new anagraficaModel(
        0,
        row.codiceFiscale,
        row.cervedItem.nome,
        row.cervedItem.cognome,
        "",
        "",
        "",
        row.cervedItem.denominazione,
        row.partitaIva,
        "",
        "",
        row.cervedItem.provincia,
        row.cervedItem.comune,
        row.cervedItem.indirizzo,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        false,
        false,
        new Date(),
        1,
        new Date(),
        0,
        !row.cervedItem.isPerson,
        89,
        provinciaId,
        comuneId,
        null,
        null,
        null,
        false,
        null,
        "",
        loggedUserId,
        "",
        new Date(),
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        1,
        1,
        0,
        "",
        "",
        "",
        "",
        false,
        "",
        "IT",
        "",
        null,
        "",
        1,
        0,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        new Date(),
        0,
        [],
        atecoFinded?.atecoCode ?? null,
        atecoFinded?.atecoTitle ?? null
      );
      handleCreateFascicolo(cervedAnag, true, "",true);
    } catch (e) {
      console.error("Error selecting Cerved row:", e);
    }
  };
  const handleGoToFascicolo = (fascicoloId) => {
    if (fascicoloId < 0) {
      setLocalStorageSelectedFascicolo(anagResultFascicolo.id);
    } else {
      setLocalStorageSelectedFascicolo(parseInt(fascicoloId));
    }
    navigate(pathFascicoloAdd, {state : {selectedValue}});
  };
  // Funzione helper per creare il titolare effettivo per le ditte individuali
  const createTitolareEffettivoAndLegaleRappr = async (fascicoloId, anagraficaDI) => {
    try {
      if(!anagraficaDI || !anagraficaDI.nome || !anagraficaDI.cognome) throw new Error("Nessun nome o cognome trovato per le anagrafiche della ditta individuale");
      const newTitEffLegalRappr = createAnagraficaModel(anagraficaDI)
      // Crea il titolare effettivo utilizzando i dati della ditta individuale
      newTitEffLegalRappr.denominazione = anagraficaDI.nome + " " + anagraficaDI.cognome;
      newTitEffLegalRappr.isPg = false; // Il titolare effettivo o legale rappresentante è sempre una persona fisica
      newTitEffLegalRappr.pIva = "";
      newTitEffLegalRappr.identifiedByUser = null;
      //Il modello viene clonato da quello della ditta, che è una persona giuridica:
      //il tipo documento va riportato sul default delle persone fisiche.
      newTitEffLegalRappr.docTipoId = getDefaultDocTipoId(false);

      let anagTitEffAndLegRappRes = await anagraficaApi.PostAnagrafica(newTitEffLegalRappr)
      if(anagTitEffAndLegRappRes){
        // Salva il titolare effettivo
        const createTitolareResp = await anagFascicoloApi.PostAnagraficaFascicolo(
          {
            anagraficaId: anagTitEffAndLegRappRes.id,
            fascicoloId: fascicoloId,
            tipoAnagId: 5,
            tipoPartId: null,
            tipoPartDesc: ""
          },
          fascicoloId
        )       
        const createLegaleRappResp = await anagFascicoloApi.PostAnagraficaFascicolo(
          {
            anagraficaId: anagTitEffAndLegRappRes.id,
            fascicoloId: fascicoloId,
            tipoAnagId: 3,
            tipoPartId: null,
            tipoPartDesc: ""
          },
          fascicoloId
        ) 
        if (createTitolareResp?.id && createLegaleRappResp?.id){
          console.log("Titolare effettivo creato con successo, ID:", createTitolareResp.id);
          console.log("Legale Rappresentante creato con successo, ID:", createLegaleRappResp.id);
        } else {
          throw new Error("Errore nella creazione del titolare effettivo");
        }
        try {
          await workspaceApi.UpdateItem(anagTitEffAndLegRappRes.id)
        } catch (error) {
          logApi.AddLog(`${error.message} while updating workspace item for anagrafica with id ${anagTitEffAndLegRappRes.id}`,"", "handleGoToFascicolo/workspaceUpdateItem", "FascicoloNew")
        }
      }
    } catch (error) {
      console.error("Errore durante la creazione del titolare effettivo o legale rappresentante:", error);
      throw error;
    }
  };

  /**
   * Per una ditta individuale Workspace espone due item con lo stesso codice fiscale:
   * il SELF_EMPLOYED_WORKER (la ditta) e il PERSON (la persona fisica). Sono esattamente
   * le due righe che il flusso DI crea comunque in locale, quindi la persona fisica va
   * costruita dal proprio item invece di clonare quello della ditta: così conserva il
   * suo workspaceId, amlId, indirizzi e dati di identificazione.
   * Ritorna il modello creato, oppure null se l'item non c'è: in quel caso il chiamante
   * ricade sulla creazione per clonazione.
   */
  const createTitolareEffettivoFromWorkspace = async (fascicoloId, cf) => {
    try {
      const wsResponse = await workspaceApi.SearchItem(cf);
      const wsPersonItem = wsResponse?.data.data.searchItem.content?.find(
        (x) => x.classifier === WS_CLASSIFIER_PERSON
      );
      if (!wsPersonItem) return null;
      const wsPerson = (await workspaceApi.GetItem(wsPersonItem.id))?.data.data.getItem ?? null;
      if (!wsPerson) return null;

      const anagraficaPF = await buildAnagraficaFromWsItem(wsPerson);
      anagraficaPF.denominazione = (anagraficaPF.nome + " " + anagraficaPF.cognome).trim();
      //Il tipo documento arriva già dall'item PERSON, che ha i suoi dati di
      //identificazione: si tocca solo se Workspace non ha dato nulla di utilizzabile.
      anagraficaPF.docTipoId = anagraficaPF.docTipoId || getDefaultDocTipoId(false);
      anagraficaPF.tenantId = tenantId;

      const anagraficaPFResult = await anagraficaApi.PostAnagrafica(anagraficaPF);
      if (!anagraficaPFResult) return null;
      const anagraficaPFModel = createAnagraficaModel(anagraficaPFResult);

      //Titolare effettivo e legale rappresentante sono la stessa persona fisica
      for (const tipoAnagId of [5, 3]) {
        await anagFascicoloApi.PostAnagraficaFascicolo(
          {
            anagraficaId: anagraficaPFModel.id,
            fascicoloId: fascicoloId,
            tipoAnagId: tipoAnagId,
            tipoPartId: null,
            tipoPartDesc: "",
          },
          fascicoloId
        );
      }
      try {
        await workspaceApi.UpdateItem(anagraficaPFModel.id);
      } catch (error) {
        logApi.AddLog(`${error.message} while updating workspace item for anagrafica with id ${anagraficaPFModel.id}`,"","createTitolareEffettivoFromWorkspace/workspaceUpdateItem","FascicoloNew");
      }
      return anagraficaPFModel;
    } catch (error) {
      logApi.AddLog(error.message, error.stack, "createTitolareEffettivoFromWorkspace", "FascicoloNew");
      return null;
    }
  };
  const handleCreateFascicolo = async (anagrafica, newWindow, fileName) => {
    try {      
      setBackDropOpen(true);
      var hasErrors = false;      

      //Le anagrafiche nuove che arrivano da Workspace portano già data di nascita,
      //utente identificante e tipo documento: azzerarle vorrebbe dire perdere il dato
      //del BE e ricalcolare la data di nascita dal codice fiscale.
      const isNewFromWorkspace = anagrafica.id === 0 && !!anagrafica.workspaceId;
      anagrafica.docRilData = null
      anagrafica.identifiedOn = null
      anagrafica.docScad = null
      if (!isNewFromWorkspace) {
        anagrafica.nascitaData = null
        anagrafica.identifiedByUser = null
      }
      //Il tipo documento ricavato da Workspace ha la precedenza: viene sovrascritto
      //solo se manca. Altrimenti il valore corretto verrebbe perso e, subito dopo,
      //rispinto sbagliato su Workspace da UpdateItem.
      if (!isNewFromWorkspace || !anagrafica.docTipoId) {
        anagrafica.docTipoId = getDefaultDocTipoId(anagrafica.isPg)
      }
      anagrafica.tenantId = tenantId
      if (
        anagrafica.nome === "" &&
        anagrafica.cognome === "" &&
        anagrafica.denominazione === ""
      ) {
        anagrafica.denominazione = anagrafica.cf;
      } else {
        if (!anagrafica.isPg) {
          anagrafica.denominazione = (anagrafica.nome + " " + anagrafica.cognome).length > 255 ? anagrafica.nome : anagrafica.nome + " " + anagrafica.cognome;
        }                   
      }

      var fascicolo = new fascicoloModel(
        0,
        tenantId,
        aziendaId,
        anagrafica.denominazione,
        new Date(),
        false,
        new Date(),
        null,
        [],
        "",
        "",
        loggedUserId
      );
      
      const fascicoloResp = await fascicoloApi.CreateFascicolo(tenantId,fascicolo)
      if(fascicoloResp){       
        let fascicoloResultModel = createFascicoloModel(fascicoloResp)
        //Aggiungo la Audit per tracciare l'azione
        const newValues = fascicoloResultModel;
        apiAddAudit.fetch({
          tenantId: tenantId,
          aziendaId: aziendaId,
          area: bsaFascicolo,
          operation: "Create",
          entityType: "fascicolo",
          entityId: fascicoloResultModel.id.toString(),
          oldValues: "",
          newValues: JSON.stringify(newValues),
        });

        //Anagrafica        
        if (anagrafica.id === 0) {
          const cfDatas = getDataFromFiscalCode(anagrafica.cf);
          if (cfDatas !== "") {
            const dataNascita = cfDatas.data.split("/");
            const sesso = cfDatas.sesso;
            const codComune = cfDatas.codComune;
            var luogoNascita = "";

            const comuneResponse = await apiBaseComuneByCodBelfiore.fetch({
              codBelfiore: codComune,
            });
            if (comuneResponse.data.errors !== undefined) {
            } else {
              comuneResponse.data.data.base_comune.forEach((item) => {
                luogoNascita = item.Nome;
              });
            }

            if (luogoNascita === "") {
              const paeseResponse = await apiBasePaeseByCodAt.fetch({
                codiceAt: codComune,
              });
              if (paeseResponse.data.errors !== undefined) {
              } else {
                paeseResponse.data.data.base_paese.forEach((item) => {
                  luogoNascita = item.Nome;
                });
              }
            }            
            
            const baseGenereListResponse = await apiBaseGenere.fetch({
              tenantId: tenantId,
            });
            //Il genere ricavato dal codice fiscale non deve sovrascrivere quello che
            //Workspace ha già fornito; interviene solo se il dato manca.
            if (baseGenereListResponse.data.data.base_genere.length > 0 && !anagrafica.genereId) {
              anagrafica.genereId = baseGenereListResponse.data.data.base_genere.filter(
                (x) => x.Value === sesso
              )[0].Id;
            }
            
            if(!anagrafica.nascitaData){
              anagrafica.nascitaData = dateHelpers.FormatToUTCWithoutTimezone(`${dataNascita[0]}/${dataNascita[1]}/${dataNascita[2]}`,false)
            }
            if (anagrafica.nascitaLuogo === "") {
              anagrafica.nascitaLuogo = luogoNascita;
            }
            if (anagrafica.genereId === 0) {
              anagrafica.genereId = null;
            }
          }                
          //Creo l'anagrafica cliente
          let anagraficaPostResult = await anagraficaApi.PostAnagrafica(anagrafica)
          if(anagraficaPostResult){
            let anagraficaResultModel = createAnagraficaModel(anagraficaPostResult)
            try{
              await workspaceApi.UpdateItem(anagraficaResultModel.id)
            } catch(error){
              logApi.AddLog(`${error.message} while updating workspace item for anagrafica with id ${anagraficaResultModel.id}`,"", "handleCreateFascicolo/workspaceUpdateItem", "FascicoloNew");
            }
            //Associo l'anagrafica cliente creata al fascicolo creato
            await anagFascicoloApi.PostAnagraficaFascicolo(
              {
                anagraficaId: anagraficaResultModel.id,
                fascicoloId: fascicoloResultModel.id,
                tipoAnagId: anagrafica.isPg ? 2 : 1,
                tipoPartId: null,
                tipoPartDesc: ""  
              },
              fascicoloResultModel.id
            )                       
            //Aggiungo la Audit per tracciare l'azione
            const newValues = anagraficaResultModel;
            apiAddAudit.fetch({
              tenantId: tenantId,
              aziendaId: aziendaId,
              area: bsaAnagrafica,
              operation: "Create",
              entityType: "anagrafica",
              entityId: fascicoloResultModel.id.toString(),
              oldValues: "",
              newValues: JSON.stringify(newValues),
            });

            // Se è una ditta individuale (DI), crea automaticamente il titolare effettivo e il legale rappresentante
            if (selectedValue === 'DI') {
              try {
                //Controllo se esiste un'anagrafica PF con lo stesso CF da collegare se non esiste la creo e la associo
                let anagraficaPFToConnect = (await anagraficaApi.GetAnagraficaFromDB(anagrafica.cf,"",false))[0]
                if(!anagraficaPFToConnect){
                  //Se la ditta arriva da Workspace la persona fisica ha un item proprio:
                  //la creo da quello, altrimenti ricado sulla clonazione della ditta.
                  const anagraficaPFFromWs = isNewFromWorkspace
                    ? await createTitolareEffettivoFromWorkspace(fascicoloResultModel.id, anagrafica.cf)
                    : null
                  if(!anagraficaPFFromWs){
                    await createTitolareEffettivoAndLegaleRappr(fascicoloResultModel.id, anagraficaResultModel);
                  }
                  
                  enqueueSnackbar("Titolare effettivo e legale rappresentante creato automaticamente per la ditta individuale", {
                    variant: "success",
                    autoHideDuration: 7000
                  });
                } else {
                  const createTitolareResp = await anagFascicoloApi.PostAnagraficaFascicolo(
                    {
                      anagraficaId: anagraficaPFToConnect.id,
                      fascicoloId: fascicoloResultModel.id,
                      tipoAnagId: 5,
                      tipoPartId: null,
                      tipoPartDesc: ""
                    },
                    fascicoloResultModel.id
                  )
                  const createLegaleRappResp = await anagFascicoloApi.PostAnagraficaFascicolo(
                    {
                      anagraficaId: anagraficaPFToConnect.id,
                      fascicoloId: fascicoloResultModel.id,
                      tipoAnagId: 3,
                      tipoPartId: null,
                      tipoPartDesc: ""
                    },
                    fascicoloResultModel.id
                  )  
                  if(!createTitolareResp && !createLegaleRappResp) {
                    enqueueSnackbar("Titolare effettivo e legale rappresentante trovato e associato correttamente al fascicolo creato", {
                      variant: "success",
                      autoHideDuration: 7000
                    });
                  }
                }
              } catch (error) {
                enqueueSnackbar("Errore nella creazione del titolare effettivo e legale rappresentante: " + error.message, {
                  variant: "error",
                });
                logApi.AddLog(error.message,"","handleCreateFascicolo/DittaIndividuale/CreazioneOAssociazioneTitEffLegalRapp","FascicoloNew")
              }
            }
          }
        } else {       
          //Associo l'anagrafica cliente esistente al fascicolo creato
          await anagFascicoloApi.PostAnagraficaFascicolo(
            {
              anagraficaId: anagrafica.id,
              fascicoloId: fascicoloResultModel.id,
              tipoAnagId: anagrafica.isPg ? 2 : 1,
              tipoPartId: null,
              tipoPartDesc: ""
            },
            fascicoloResultModel.id
          )                 
          //Controllo se esiste un'anagrafica PF con lo stesso CF da collegare
          if(selectedValue === "DI"){
            let anagraficaPFToConnect = (await anagraficaApi.GetAnagraficaFromDB(anagrafica.cf,"",false))[0]
            if(anagraficaPFToConnect){
              //Aggiungo l'anagrafica trovata come legale rappresentante
              let addLegRappResponse = await anagFascicoloApi.PostAnagraficaFascicolo({
                  anagraficaId: anagraficaPFToConnect.id,
                  fascicoloId: fascicoloResultModel.id,
                  tipoAnagId: 3,
                  tipoPartId: null,
                  tipoPartDesc: ""
                },
                fascicoloResultModel.id
              )
              //Aggiungo l'anagrafica trovata come titolare effettivo 
              let addTitEffResponse = await anagFascicoloApi.PostAnagraficaFascicolo({
                  anagraficaId: anagraficaPFToConnect.id,
                  fascicoloId: fascicoloResultModel.id,
                  tipoAnagId: 5,
                  tipoPartId: null,
                  tipoPartDesc: ""
                },
                fascicoloResultModel.id
              )
              if(addLegRappResponse && addTitEffResponse){
                enqueueSnackbar("Anagrafica persona fisica trovata e associata come legale rappresentante e titolare effettivo alla ditta individuale creata", {
                  variant: "success",
                  autoHideDuration: 7000
                });
              }
            }
          }          
        }        

        //Aggiungo gli allegati se ce ne sono
        try {
          if (fileName !== "") {
            const fileArr = files.filter((x) => x.name === fileName);
            if (fileArr.length > 0) {
              const file = fileArr[0];              
              const displayedFileName = file.name;

              const encFileBase64 = await fascicoloHelpers.EncodeBase64(file);
              const encFile = encFileBase64[0];

              const encFileBase64String = encFile.encrypt
                .substring(
                  encFile.encrypt.indexOf("base64,"),
                  encFile.encrypt.length
                )
                .split(",")[1];
              const fileJson = {
                file: encFileBase64String,
                contentType: file.type,
              };              
            }
          }
        } catch (e) {
          logApi.AddLog(e.message,e.stack,"handleCreateFascicolo/AddAllegato", "FascicoloNew")          
        }
        
        gainsightService.track("AML_Fascicolo_Create_Success");
        handleGoToFascicolo(fascicoloResultModel.id, newWindow);
      }

      if (hasErrors) {
          enqueueSnackbar(t("error.generale"), { variant: "error" });
      }
    }
    catch (e) {
      logApi.AddLog(e.message,e.stack,"handleCreateFascicolo", "FascicoloNew")           
      enqueueSnackbar(t("error.generale"), { variant: "error" });
      gainsightService.track("AML_Fascicolo_Create_Error", { error: e.message });
    }
    finally {
        setBackDropOpen(false);
        setIsDataLoaded(true);
    }
  };  
  //#endregion

  const handleResetForm = () => {
    resetSearchResults();
    setAdePIvaStatus(null)
    setAdeCfStatus(null);
    resetSearchGeography();
    setCurrentAnagrafica({
      cf: "",
      pIva: "",
      denominazione: "",
      nome: "",
      cognome: "",
      isPg: false,
      id: 0
    });    
    companyRef.current?.focus()
  };

  const [files/*, setFiles*/] = useState([]);

  // Il pulsante Cerca resta disabilitato finché almeno un campo di ricerca non ha ≥ 4 caratteri.
  const searchFieldValues =
    selectedValue === "PF"
      ? [currentAnagrafica.cf, currentAnagrafica.nome, currentAnagrafica.cognome]
      : selectedValue === "DI"
      ? [
          currentAnagrafica.cf,
          currentAnagrafica.pIva,
          currentAnagrafica.denominazione,
          currentAnagrafica.nome,
          currentAnagrafica.cognome,
        ]
      : [currentAnagrafica.cf, currentAnagrafica.pIva, currentAnagrafica.denominazione];
  const canSearch = searchFieldValues.some((v) => (v || "").trim().length >= 4);
  const hasSearchCriteria = searchFieldValues.some((value) => (value || "").trim().length > 0);
  const manualRequiredValues = [
    currentAnagrafica.cf,
    ...(isPG ? [currentAnagrafica.pIva, currentAnagrafica.denominazione] : []),
    ...(!isPG || selectedValue === "DI"
      ? [currentAnagrafica.nome, currentAnagrafica.cognome]
      : []),
  ];
  const isManualDataComplete = manualRequiredValues.every((value) => (value || "").trim().length > 0);
  //Per le PG il campo CF contiene la P.IVA (VAT numerico), quindi si valida come tale.
  const getManualValidationError = () => {
    const cf = (currentAnagrafica.cf || "").trim();
    const pIva = (currentAnagrafica.pIva || "").trim();
    const isCfValid =
      selectedValue === "PG" ? isValidItalianVAT(cf) : isValidCodiceFiscale(cf);
    if (!isCfValid) return "Il Codice Fiscale non è in un formato valido";
    if (isPG && !isValidItalianVAT(pIva)) return "La Partita IVA non è in un formato valido";
    return null;
  };
  const handleManualCreateFascicolo = () => {
    const validationError = getManualValidationError();
    if (validationError) {
      enqueueSnackbar(validationError, { variant: "warning" });
      return;
    }
    handleCreateFascicolo(currentAnagrafica, files.length > 1, "");
  };

  //Evita che provincia e comune restino applicati dopo lo svuotamento degli altri criteri.
  useEffect(() => {
    if (!hasSearchCriteria && (searchProvincia || searchComune)) resetSearchGeography();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearchCriteria, searchProvincia, searchComune]);

  //#region Return Graphics
  return (
    <Fragment>
      <VaporPage
        title={t("fascicolo.titoloAdd")}
        headerLeft={
          <Button
            variant="text"
            size="medium"
            color="primary"
            onClick={handleGoHome}
            sx={{ ml: 2 }}
          >
            {t("autovalutazione.autovalutazioneAdd.back")}
          </Button>
        }
        headerRight={
          <Button
            variant="text"
            size="medium"
            color="primary"
            onClick={handleGoHome}
            sx={{ ml: 2, visibility: "hidden" }}
          >
            {t("autovalutazione.autovalutazioneAdd.back")}
          </Button>
        }
      >
        <Loading open={backDropOpen} />

        {/* Ricerca in un unico passaggio: selettore sopra, campi affiancati, bottone a lato */}
        <Stack direction="column" spacing={3} sx={{ p: 2, mt: 1 }}>
          <ToggleButtonGroup
            value={selectedValue}
            exclusive
            onChange={handleTipoSoggettoChange}
            size="small"
          >
            <ToggleButton value="PG" id="checkPg">
              Persona giuridica
            </ToggleButton>
            <ToggleButton value="DI" id="checkDi">
              Ditta individuale
            </ToggleButton>
            <ToggleButton value="PF" id="checkPf">
              Persona fisica
            </ToggleButton>
          </ToggleButtonGroup>

          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            flexWrap="wrap"
            useFlexGap
            sx={{ "& > .MuiGrid-item": { flexBasis: "auto", maxWidth: "none", width: "auto", mb: 0 } }}
          >
            {!isPG ? (
              <Fragment>
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <TextField
                    id="txtSearchCognome"
                    label={t("impostazioni.utenti.form.surname")}
                    size="small"
                    onChange={handleChange}
                    inputProps={{ autocomplete: "nope",maxLength:255 }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <TextField
                    id="txtSearchNome"
                    label={t("impostazioni.utenti.form.name")}
                    size="small"
                    onChange={handleChange}
                    inputProps={{ autocomplete: "nope",maxLength:255 }}
                  />
                </Grid>                      
              </Fragment>
            ) : (                              
              <Grid item xs={12} sx={{ mb: 2 }}>
                <TextField
                  id="txtSearchDenominazione"
                  label={t("anagrafica.ImportDesktop.denom")}
                  value={currentAnagrafica.denominazione}
                  size="small"
                  inputRef={companyRef}
                  onChange={handleChange}
                  inputProps={{ autocomplete: "nope",maxLength:255 }}
                />
              </Grid>              
            )}
            
            <Stack direction="column" spacing={1}>
              <TextField
                id="txtSearchCf"
                label={t("anagrafica.ImportDesktop.codFisc")}
                size="small"
                value={currentAnagrafica.cf || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ autocomplete: "nope", maxLength: 16, ...(selectedValue === "PG" && { pattern: "[0-9]*" }) }}
              />
              {adeCfStatus ? (
                <VaporTag
                  variant="ghost"
                  type={adeCfStatus.valid ? "islamicGreen" : "rustyRed"}
                  label={
                    adeCfStatus.valid
                      ? "Codice fiscale valido (ADE)"
                      : "Codice fiscale non riconosciuto da ADE"
                  }
                />
              ) : null}
            </Stack>
                  
            {isPG ? (
              <Stack direction="column" spacing={1}>
                <TextField
                  id="txtSearchPIva"
                  label={t("fascicolo.fascicoloNew.vatNumber")}
                  size="small"
                  value={currentAnagrafica.pIva || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  inputProps={{ autocomplete: "nope", maxLength: 11 }}
                />
                {adePIvaStatus ? (
                  <VaporTag
                    variant="ghost"
                    type={adePIvaStatus.valid ? "islamicGreen" : "rustyRed"}
                    label={
                      adePIvaStatus.valid
                        ? "Partita IVA valida (ADE)"
                        : "Partita IVA non valida (ADE)"
                    }
                  />
                ) : null}
              </Stack>
            ) : null}

            {isPG ? (
              <Fragment>
                <Autocomplete
                  id="ddlSearchProvincia"
                  size="small"
                  sx={{ width: 230, maxWidth: "100%" }}
                  options={baseProvinciaList}
                  value={searchProvincia}
                  disabled={!hasSearchCriteria}
                  disableClearable={false}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.nome}
                  filterOptions={filterGeographyOptions}
                  onChange={handleSearchProvinciaChange}
                  renderInput={(params) => <TextField {...params} label="Provincia" />}
                />
                <Autocomplete
                  id="ddlSearchComune"
                  size="small"
                  sx={{ width: 230, maxWidth: "100%" }}
                  options={baseComuneList}
                  value={searchComune}
                  disabled={!hasSearchCriteria || !searchProvincia || comuniLoading}
                  disableClearable={false}
                  loading={comuniLoading}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.nome}
                  filterOptions={filterGeographyOptions}
                  onChange={(event, comune) => setSearchComune(comune)}
                  renderInput={(params) => <TextField {...params} label="Comune" />}
                />
              </Fragment>
            ) : null}

            <Button
              variant="outlined"              
              onClick={handleSearchClick}
              size="small"
              endIcon={
                <SearchIcon sx={{ color: "primary" }} />
              }
              sx={{ mt: 3.25 }}
              disabled={!canSearch}
            >
              {t("fascicolo.fascicoloAdd.cerca")}
            </Button>
          </Stack>

          {unifiedResults.length > 0 ? (
            <FascicoloSearchResultsGrid
              results={unifiedResults}
              onRowSelect={handleRowSelect}
              onExtendToCerved={isPG ? handleExtendToCerved : undefined}
              cervedSearched={cervedSearched}
              handleResetForm={handleResetForm}
            />
          ) : null}

          {showNoAnagFound &&
          unifiedResults.length <= 0 &&
          importResultList.length <= 0 ? (
                <Grid container spacing={2}>
                  <Grid item xs={4}></Grid>
                  <Grid item xs={4}>
                    <Grid item xs={12} sx={{ mb: 2 }}>
                      <Alert variant="outlined" severity="warning">
                        {t("fascicolo.fascicoloNew.notFoundExistingRecord")}
                      </Alert>
                    </Grid>

                    <Grid item xs={12} sx={{ mb: 2 }}>
                      <TextField
                        id="txtCf"
                        label="Codice Fiscale"
                        size="small"
                        required
                        defaultValue={currentAnagrafica.cf}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        inputProps={{ autocomplete: "nope",maxLength:16 }}
                      />
                    </Grid>

                    {isPG ? (
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        <TextField
                          id="txtPIva"
                          label="P. Iva"
                          size="small"
                          required={isPG}
                          defaultValue={currentAnagrafica.pIva}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          inputProps={{ autocomplete: "nope",maxLength:11 }}
                        />
                      </Grid>
                    ) : null}

                    {!isPG ? (
                      <Fragment>
                        <Grid item xs={12} sx={{ mb: 2 }}>
                          <TextField
                            id="txtCognome"
                            label="Cognome"
                            size="small"
                            required
                            defaultValue={currentAnagrafica.cognome}
                            onChange={handleChange}
                            inputProps={{ autocomplete: "nope",maxLength:255 }}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ mb: 2 }}>
                          <TextField
                            id="txtNome"
                            label="Nome"
                            size="small"
                            required
                            defaultValue={currentAnagrafica.nome}
                            onChange={handleChange}
                            inputProps={{ autocomplete: "nope",maxLength:255 }}
                          />
                        </Grid>
                      </Fragment>
                    ) : (
                      <>
                        {selectedValue === "DI" && (
                          <Fragment>
                            <Grid item xs={12} sx={{ mb: 2 }}>
                              <TextField
                                id="txtCognome"
                                label="Cognome"
                                size="small"
                                required
                                defaultValue={currentAnagrafica.cognome}
                                onChange={handleChange}
                                inputProps={{ autocomplete: "nope",maxLength:255 }}
                              />
                            </Grid>
                            <Grid item xs={12} sx={{ mb: 2 }}>
                              <TextField
                                id="txtNome"
                                label="Nome"
                                size="small"
                                required
                                defaultValue={currentAnagrafica.nome}
                                onChange={handleChange}
                                inputProps={{ autocomplete: "nope",maxLength:255 }}
                              />
                            </Grid>
                          </Fragment>
                        )}
                        
                        <Grid item xs={12} sx={{ mb: 2 }}>
                          <TextField
                            id="txtDenominazione"
                            label="Denominazione"
                            size="small"
                            required
                            defaultValue={currentAnagrafica.denominazione}
                            onChange={handleChange}
                            inputProps={{ autocomplete: "nope",maxLength:255 }}
                          />
                        </Grid>
                      </>
                    )}                                       

                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleManualCreateFascicolo}
                        disabled={
                          !isIdentifiedByEnableProf ||
                          disableSearch === true ||
                          !isManualDataComplete
                        }
                      >
                        {t("fascicolo.fascicoloNew.creaFasicolo")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleResetForm}
                      >
                        {t("fascicolo.fascicoloNew.newSearch")}
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid item xs={4}></Grid>
                </Grid>
              ) : null}

              {importResultList.length > 0 ? (
                <Grid container spacing={2}>
                  <Grid item xs={4}></Grid>
                  <Grid item xs={4}>
                    <Grid item xs={12}>
                      {importResultList.map((item) => (
                        <ExtendedAccordion
                          key={uuidv4()}
                          sx={{ width: "100%" }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <ExtendedTypography
                              variant="titleSmall"
                              color="primary.main"
                              sx={{ width: "100% !important" }}
                            >
                              {item.anagrafica.cf} |{" "}
                              {item.anagrafica.isPg
                                ? item.anagrafica.denominazione
                                : item.anagrafica.nome +
                                  " " +
                                  item.anagrafica.cognome}
                            </ExtendedTypography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack direction="column" spacing={2}>
                              <ExtendedTypography
                                variant="body"
                                color="primary.main"
                                sx={{ width: "100% !important" }}
                              >
                                {item.anagrafica.isPg
                                  ? item.anagrafica.denominazione
                                  : item.anagrafica.nome +
                                    " " +
                                    item.anagrafica.cognome}
                                <br />
                                {item.anagrafica.isPg
                                  ? "C.F.: " +
                                    item.anagrafica.cf +
                                    " - P. Iva: " +
                                    item.anagrafica.pIva
                                  : "C.F.: " + item.anagrafica.cf}
                                <br />
                                {!item.anagrafica.isPg
                                  ? "Nato a " +
                                    item.anagrafica.nascitaLuogo +
                                    " il " +
                                    new Date(
                                      item.anagrafica.nascitaData
                                    ).toLocaleDateString()
                                  : ""}
                                <br />
                                {item.anagrafica.isPg
                                  ? "Iscritta al R.I. di " +
                                    item.anagrafica.reaComune +
                                    " - N.: " +
                                    item.anagrafica.reaN
                                  : null}
                                <br />
                                {item.anagrafica.isPg
                                  ? "Sede legale: " +
                                    item.anagrafica.addrIndirizzo +
                                    " " +
                                    item.anagrafica.addrN +
                                    " - " +
                                    item.anagrafica.addrCap +
                                    " " +
                                    item.anagrafica.addrComune +
                                    " (" +
                                    item.anagrafica.addrProvincia +
                                    ")"
                                  : "Indirizzo di residenza: " +
                                    item.anagrafica.addrIndirizzo +
                                    " " +
                                    item.anagrafica.addrN +
                                    " - " +
                                    item.anagrafica.addrCap +
                                    " " +
                                    item.anagrafica.addrComune +
                                    " (" +
                                    item.anagrafica.addrProvincia +
                                    ")"}
                              </ExtendedTypography>
                              {item.fascicolo.id > 0 ? (
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handleGoToFascicolo(item.fascicolo.id, true)
                                  }
                                >
                                  {t("fascicolo.fascicoloNew.goToFolder", "Vai al fascicolo")}
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      handleCreateFascicolo(
                                        item.anagrafica,
                                        files.length > 1,
                                        item.fileName
                                      )
                                    }
                                    disabled = {!isIdentifiedByEnableProf}
                                  >
                                    {t("fascicolo.fascicoloNew.creaFasicolo")}
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleResetForm}
                                  > 
                                    {t("fascicolo.fascicoloNew.newSearch")}
                                  </Button>
                                </>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </ExtendedAccordion>
                      ))}
                    </Grid>
                  </Grid>
                  <Grid item xs={4}></Grid>
                </Grid>
              ) : null}
        </Stack>
      </VaporPage>
    </Fragment>
  );
  //#endregion
};
